import React from 'react';
import moment from 'moment';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Box,
  Alert,
} from '@mui/material';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import { SalesOrder } from 'services/salesOrders';
import { BulkQuickFulfillProgressDetail } from '../types';

interface BulkQuickFulfillModalProps {
  isStopping?: boolean;
  open: boolean;
  eligibleOrders: SalesOrder[];
  ineligibleOrders: { order: SalesOrder; reason: string }[];
  stage: 'confirm' | 'progress' | 'summary';
  progressDetails: BulkQuickFulfillProgressDetail[];
  onClose: () => void;
  onConfirmAndProcess: () => void;
  onStopProcessing?: () => void;
  estimatedTimePerOrderSeconds: number;
}

const formatDisplayTime = (totalSeconds: number): string | null => {
  if (totalSeconds <= 30) {
    return null;
  }

  const duration = moment.duration(totalSeconds, 'seconds');

  if (totalSeconds < 60) {
    const secs = Math.round(duration.asSeconds());
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  }

  const minutes = duration.minutes();
  const seconds = duration.seconds();

  let timeString = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  if (seconds > 0) {
    timeString += ` ${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  return timeString;
};

const BulkQuickFulfillModal: React.FC<BulkQuickFulfillModalProps> = ({
  open,
  eligibleOrders,
  ineligibleOrders,
  stage,
  progressDetails,
  onClose,
  onConfirmAndProcess,
  onStopProcessing,
  isStopping,
  estimatedTimePerOrderSeconds,
}) => {
  const estimatedTimeSeconds =
    eligibleOrders.length * estimatedTimePerOrderSeconds;

  const totalOrdersToProcess = eligibleOrders.length;
  const ordersProcessed = progressDetails.filter(
    (p) => p.status === 'fulfilled' || p.status === 'error'
  ).length;
  const progressPercentage =
    totalOrdersToProcess > 0
      ? (ordersProcessed / totalOrdersToProcess) * 100
      : 0;

  const currentFulfillingOrder = progressDetails.find(
    (p) => p.status === 'fulfilling'
  );

  const renderConfirmStage = () => (
    <>
      <DialogTitle>Confirm Bulk Quick Fulfilment</DialogTitle>
      <DialogContent dividers>
        {ineligibleOrders.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following orders are not eligible for Quick Fulfil:
            </Alert>
            <List
              dense
              sx={{
                maxHeight: 150,
                overflow: 'auto',
                mb: 2,
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            >
              {ineligibleOrders.map(({ order, reason }) => (
                <ListItem key={order.id} dense>
                  <ListItemText
                    primary={`SO #${order.number || order.id}`}
                    secondary={reason}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {eligibleOrders.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are about to Quick Fulfil {eligibleOrders.length} sales
              order(s):
            </Alert>
            <List
              dense
              sx={{
                maxHeight: 150,
                overflow: 'auto',
                mb: 2,
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            >
              {eligibleOrders.map((order) => (
                <ListItem key={order.id} dense>
                  <ListItemText primary={`SO #${order.number || order.id}`} />
                </ListItem>
              ))}
            </List>
            {formatDisplayTime(estimatedTimeSeconds) && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Estimated time to process: approximately{' '}
                {formatDisplayTime(estimatedTimeSeconds)}.
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Do you want to proceed?
            </Typography>
          </>
        ) : (
          <Alert severity="error">
            No orders are eligible for Quick Fulfilment.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <FBOButton
          variant="secondary"
          color="neutral"
          size="medium"
          onClick={onClose}
          data-qa="bulk-qf-cancel-btn"
        >
          Cancel
        </FBOButton>
        <FBOButton
          variant="primary"
          color="positive"
          size="medium"
          onClick={onConfirmAndProcess}
          disabled={eligibleOrders.length === 0}
          data-qa="bulk-qf-confirm-process-btn"
        >
          Confirm and Process
        </FBOButton>
      </DialogActions>
    </>
  );

  const renderProgressStage = () => (
    <>
      <DialogTitle>Processing Bulk Quick Fulfilment</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Processing {ordersProcessed} of {totalOrdersToProcess} orders...
        </Typography>
        {currentFulfillingOrder && (
          <Typography
            variant="caption"
            display="block"
            gutterBottom
            sx={{ mb: 1 }}
          >
            Currently fulfilling: SO #
            {currentFulfillingOrder.number || currentFulfillingOrder.orderId}
          </Typography>
        )}
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress variant="determinate" value={progressPercentage} />
        </Box>
        <List
          dense
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          {progressDetails.map((detail) => (
            <ListItem key={detail.orderId} dense>
              <ListItemText
                primary={`SO #${detail.number || detail.orderId}`}
                secondary={`Status: ${detail.status}${
                  detail.status === 'error' ? ` - ${detail.message}` : ''
                }`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        {onStopProcessing && (
          <FBOButton
            variant="secondary"
            color="negative"
            size="medium"
            onClick={onStopProcessing}
            loading={isStopping}
            disabled={!onStopProcessing || isStopping}
            data-qa="bulk-qf-stop-btn"
          >
            {isStopping ? 'Stopping...' : 'Stop'}
          </FBOButton>
        )}
      </DialogActions>
    </>
  );

  const renderSummaryStage = () => {
    const fulfilledCount = progressDetails.filter(
      (p) => p.status === 'fulfilled'
    ).length;
    const errorCount = progressDetails.filter(
      (p) => p.status === 'error'
    ).length;
    const cancelledCount = progressDetails.filter(
      (p) => p.status === 'cancelled'
    ).length;
    const totalItems = progressDetails.length;

    let calculatedAlertSeverity: 'success' | 'info' | 'warning' | 'error' =
      'success';

    if (totalItems === 0) {
      calculatedAlertSeverity = 'info';
    } else if (errorCount > 0) {
      calculatedAlertSeverity =
        fulfilledCount > 0 || cancelledCount > 0 ? 'warning' : 'error';
    } else if (fulfilledCount > 0) {
      calculatedAlertSeverity = cancelledCount > 0 ? 'warning' : 'success';
    } else if (cancelledCount > 0 && cancelledCount === totalItems) {
      calculatedAlertSeverity = 'info';
    }

    const fulfilledItems = progressDetails.filter(
      (p) => p.status === 'fulfilled'
    );
    const otherItemsRaw = progressDetails.filter(
      (p) => p.status !== 'fulfilled'
    );

    const sortedOtherItems = [...otherItemsRaw].sort((a, b) => {
      const isAError = a.status === 'error';
      const isBError = b.status === 'error';
      if (isAError && !isBError) return -1;
      if (!isAError && isBError) return 1;
      return 0;
    });

    return (
      <>
        <DialogTitle>Bulk Quick Fulfilment Summary</DialogTitle>
        <DialogContent dividers>
          <Alert severity={calculatedAlertSeverity} sx={{ mb: 2 }}>
            Processing Complete.
            {fulfilledCount > 0 &&
              ` Successfully fulfilled ${fulfilledCount} order(s).`}
            {errorCount > 0 && ` Failed to fulfil ${errorCount} order(s).`}
            {cancelledCount > 0 &&
              fulfilledCount === 0 &&
              errorCount === 0 &&
              ` All ${cancelledCount} processed order(s) were cancelled or not attempted.`}
          </Alert>

          {fulfilledItems.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, mb: 1, color: 'success.main' }}
              >
                Successfully Fulfilled ({fulfilledItems.length})
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  mb: 2,
                }}
              >
                {fulfilledItems.map((detail) => (
                  <ListItem key={detail.orderId} dense>
                    <ListItemText
                      primary={`SO #${detail.number || detail.orderId}`}
                      secondary="Status: Fulfilled"
                      sx={{ color: 'success.main' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {sortedOtherItems.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 2,
                  mb: 1,
                  color:
                    errorCount > 0
                      ? 'error.main'
                      : cancelledCount > 0
                      ? 'info.main'
                      : 'text.primary',
                }}
              >
                Other Outcomes ({sortedOtherItems.length})
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
              >
                {sortedOtherItems.map((detail) => (
                  <ListItem key={detail.orderId} dense>
                    <ListItemText
                      primary={`SO #${detail.number || detail.orderId}`}
                      secondary={
                        detail.status === 'error'
                          ? `Status: Error - ${detail.message}`
                          : detail.status === 'cancelled'
                          ? 'Status: Not Attempted (Cancelled)'
                          : `Status: ${detail.status}` // Fallback for any other non-fulfilled status
                      }
                      sx={{
                        color:
                          detail.status === 'error'
                            ? 'error.main'
                            : detail.status === 'cancelled'
                            ? 'info.main'
                            : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {progressDetails.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No orders were processed.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <FBOButton
            variant="primary"
            color="positive"
            size="medium"
            onClick={onClose}
            data-qa="bulk-qf-close-btn"
          >
            Close
          </FBOButton>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={stage === 'progress' ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      {stage === 'confirm' && renderConfirmStage()}
      {stage === 'progress' && renderProgressStage()}
      {stage === 'summary' && renderSummaryStage()}
    </Dialog>
  );
};

export default BulkQuickFulfillModal;
